import { memo, useState } from 'react';
import { useRouter } from 'next/router';

import { Checkbox, FieldSet, Label, LabeledControl, Legend, Toast, useToast } from '@zillow/constellation-10';

import {
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCentralAirConditioning,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWallAirConditioning,
  useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWindowAirConditioning,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';
import { DatarouterError } from '@zg-rentals/web-base';

import { useGetListingDetails } from '../../../../hooks/queries/properties';
import { useBatchMutationToast } from '../../../../hooks/useBatchMutationToast';
import { usePropertyEditMutation } from '../../../../hooks/usePropertyEditMutation';
import { type AmenityFlagSave } from '../../utils';
import { FeedOverrideSection } from '@zg-rentals/ts-rental-manager-api-client';

import { useFeedAmenityMutation } from '../../../../hooks/useFeedAmenityMutation';
import { useIsInputFormDisabled } from '../../../../hooks/useIsInputFormDisabled';

import {
  AIR_CONDITIONING_KEYS,
  AIR_CONDITIONING_METADATA,
  type AirConditioningKey,
} from '../../../../constants/KeyFeaturesConstants';

import { useAuth } from '../../../AuthProvider';

type LocalState = Record<AirConditioningKey, boolean>;

const AirConditioningAmenities = (): JSX.Element | null => {
  const router = useRouter();
  const { user } = useAuth();
  const { enqueueToast } = useToast();

  const listingAlias = router.query.listingAlias as string;
  const { data: listingDetails } = useGetListingDetails(listingAlias);

  const airConditioningTypes = listingDetails?.buildingAttributes?.airConditioningTypes;
  const canUpdateListing = user?.features?.canUpdateListing ?? false;

  const [localState, setLocalState] = useState<LocalState>({
    hasCentral: airConditioningTypes?.hasCentral ?? false,
    hasWall: airConditioningTypes?.hasWall ?? false,
    hasWindow: airConditioningTypes?.hasWindow ?? false,
  });

  const { startRequest, handleSuccess, handleError } = useBatchMutationToast({
    successMessage: 'Air conditioning updated',
  });

  const isInputFormDisabled = useIsInputFormDisabled(listingDetails, canUpdateListing, FeedOverrideSection.AMENITIES);
  const { isFeedListing, setFeedAmenity } = useFeedAmenityMutation();

  const { mutateAsync: updateCentralAC } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCentralAirConditioning,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasCentralAirConditioning',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateWallAC } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWallAirConditioning,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWallAirConditioning',
        details: { listingAlias },
      },
    },
  });

  const { mutateAsync: updateWindowAC } = usePropertyEditMutation({
    mutationHook: useRentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWindowAirConditioning,
    skipToast: true,
    meta: {
      context: {
        api: 'RentalManagerUserPropertyEditAmenitiesV2WebApiHandler_hasWindowAirConditioning',
        details: { listingAlias },
      },
    },
  });

  const mutationMap = {
    hasCentral: updateCentralAC,
    hasWall: updateWallAC,
    hasWindow: updateWindowAC,
  } as unknown as Record<keyof LocalState, AmenityFlagSave>;

  const handleCheckboxChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fieldName = event.target.name as AirConditioningKey;
    const checked = event.target.value === 'true';
    const previousValue = !checked;

    startRequest();
    setLocalState((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      const metadata = AIR_CONDITIONING_METADATA[fieldName];
      if (isFeedListing) {
        await setFeedAmenity(metadata.apiKey, checked);
      } else {
        await mutationMap[fieldName]({
          postData: {
            [metadata.apiKey]: checked,
            listingAlias,
          },
        });
      }
      handleSuccess();
    } catch (err) {
      handleError();
      setLocalState((prev) => ({ ...prev, [fieldName]: previousValue }));
      const errorMessage = DatarouterError.getDisplayMessage(
        err,
        'Something went wrong, refresh the page and try again',
      );
      enqueueToast(<Toast tone="critical">{errorMessage}</Toast>);
    }
  };

  return (
    <FieldSet legend={<Legend>Air conditioning</Legend>} disabled={isInputFormDisabled}>
      {AIR_CONDITIONING_KEYS.map((fieldName) => {
        const metadata = AIR_CONDITIONING_METADATA[fieldName];
        return (
          <LabeledControl
            key={`airConditioningTypes-${metadata.label}`}
            label={<Label>{metadata.label}</Label>}
            control={
              <Checkbox
                name={fieldName}
                value={String(!localState[fieldName])}
                checked={localState[fieldName]}
                onChange={handleCheckboxChange}
              />
            }
          />
        );
      })}
    </FieldSet>
  );
};

export default memo(AirConditioningAmenities);
